// src/components/pages/portfolios/partials/PortfolioRadarChart.js

import React, { useState, useMemo, useEffect } from 'react';
import {
  ResponsiveContainer, Radar, RadarChart, PolarGrid, 
  PolarAngleAxis, PolarRadiusAxis, Tooltip
} from 'recharts';
import styles from './../style/PortfolioDetail.module.css'; 

const PERIODS = ['1w','2w','1m', '3m', '6m', '1y'];

// 간단한 정보 아이콘 SVG 컴포넌트 (외부 라이브러리 의존성 제거)
const InfoIcon = () => (
  <svg 
    width="16" height="16" viewBox="0 0 24 24" 
    fill="none" stroke="currentColor" strokeWidth="2" 
    strokeLinecap="round" strokeLinejoin="round"
    style={{ marginLeft: '6px', cursor: 'pointer', verticalAlign: 'middle', color: '#888' }}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

// 💡 1단계: Custom Hook 정의
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
}

export default function PortfolioRadarChart({ po, t }) {
  const [period, setPeriod] = useState('1w');

  const windowWidth = useWindowWidth(); // 💡 현재 화면 너비 가져오기

  // 💡 2단계: 화면 너비에 따른 outerRadius 계산
  const chartOuterRadius = useMemo(() => {
    // 700px 기준으로 조건 분기
    if (windowWidth >= 700) {
      return '80%'; // 700px 이상일 때 큰 차트 (80%)
    } else {
      return '60%'; // 700px 미만일 때 작은 차트 (60%)
    }
  }, [windowWidth]); // windowWidth가 변할 때마다 재계산

  // 지표별 설명 데이터 (번역 키를 사용하거나 직접 텍스트 입력)
  const metricInfos = [
    { label: t('portfolioDetail.metrics.return'), desc: t('portfolioDetail.tooltips.return', '동일 기간 수익률의 백분위 점수입니다.') },
    { label: t('portfolioDetail.metrics.sharpe'), desc: t('portfolioDetail.tooltips.sharpe', '위험 대비 수익률(효율성) 점수입니다.') },
    { label: t('portfolioDetail.metrics.calmar'), desc: t('portfolioDetail.tooltips.calmar', '최대 낙폭 대비 수익률 점수입니다.') },
    { label: t('portfolioDetail.metrics.stability'), desc: t('portfolioDetail.tooltips.stability', '변동성이 낮을수록 높은 점수를 받습니다.') },
    { label: t('portfolioDetail.metrics.defense'), desc: t('portfolioDetail.tooltips.defense', '최대 낙폭(MDD)이 적을수록 높은 점수를 받습니다.') },
  ];

  

  // 차트 데이터 가공
  const data = useMemo(() => {
    const metrics = [
      { key: 'ret', label: t('portfolioDetail.metrics.return') },
      { key: 'sharpe', label: t('portfolioDetail.metrics.sharpe') },
      { key: 'calmar', label: t('portfolioDetail.metrics.calmar') },
      { key: 'vol', label: t('portfolioDetail.metrics.stability') },
      { key: 'mdd', label: t('portfolioDetail.metrics.defense') },
    ];

    return metrics.map(m => {
      const fieldName = `${m.key}_${period}_perc`;
      const value = po[fieldName] !== undefined ? parseFloat(po[fieldName]) : 0;
      
      return {
        subject: m.label,
        A: value,
        fullMark: 100,
        ref: 50, 
      };
    });
  }, [po, period, t]);

  console.log('Radar',po)

  const hasData = data.some(d => d.A > 0);
  const translatedPeriod = t(`portfolioDetail.periods.${period}`);

  return (
    <div className={styles.chartBlock}>
      <div className={styles.headerRow}>
        {/* 제목 + 정보 아이콘 영역 */}
        <div className={styles.titleContainer}>
          <h3 className={styles.subTitle}>
            {t('portfolioDetail.performanceRadarTitle')}
          </h3>
          
          {/* 툴팁 래퍼 */}
          <div className={styles.tooltipWrapper}>
            <InfoIcon />
            <div className={styles.tooltipContent}>
              <h4 className={styles.tooltipTitle}>{t('portfolioDetail.tooltipTitle', '지표 상세 설명')}</h4>
              <ul className={styles.tooltipList}>
                {metricInfos.map((info, idx) => (
                  <li key={idx}>
                    <strong>{info.label}</strong>: {info.desc}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
        
      {/* 기간 선택 탭 */}
      <div className={styles.periodSelector}>
        {PERIODS.map(p => {
          const dataFieldName = `ret_${p}_perc`;
          const isPeriodDataAvailable = po[dataFieldName] !== undefined && parseFloat(po[dataFieldName]) > 0;
          
          return (
            <button
              key={p}
              className={`${styles.periodButton} ${period === p ? styles.active : ''} ${!isPeriodDataAvailable ? styles.disabled : ''}`} 
              onClick={() => setPeriod(p)}
              disabled={!isPeriodDataAvailable}
            >
              {t(`portfolioDetail.periods.${p}`)}
            </button>
          );
        })}
      </div>

      <div className={styles.radarContainer}>
        {hasData ? (
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart cx="50%" cy="50%" outerRadius={chartOuterRadius} data={data}>
              <PolarGrid stroke="#e0e0e0" />
              <PolarAngleAxis 
                dataKey="subject" 
                tick={{ 
                  fill: '#888', 
                  fontSize: 12,

                }}
                
                />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
              
              <Radar
                dataKey="ref"
                name={t('portfolioDetail.referenceLine')}
                stroke="#ff4d4d"
                strokeWidth={1}
                strokeDasharray="5 5"
                fill="none"
                dot={false}
              />
              
              <Radar
                name={t('portfolioDetail.percentile')}
                dataKey="A"
                stroke="#8884d8"
                strokeWidth={2}
                fill="#8884d8"
                fillOpacity={0.5}
              />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === t('portfolioDetail.referenceLine')) {
                    return [`50 / 100`, t('portfolioDetail.referenceLine')];
                  }
                  return [`${value} / 100`, t('portfolioDetail.percentileScore')];
                }}
                contentStyle={{ backgroundColor: '#333', border: 'none', borderRadius: '4px', color: '#fff' }}
              />
            </RadarChart>
          </ResponsiveContainer>
        ) : (
          <div className={styles.noDataMessage}>
            {t('portfolioDetail.noPeriodData', { period: t(`portfolioDetail.periods.${period}`) })}
          </div>
        )}
      </div>
      
      {hasData && (
        <p className={styles.chartCaption}>
          * {t('portfolioDetail.radarCaption', { period: translatedPeriod })}
          <br/>
          * <span style={{color: '#ff4d4d', fontWeight: 'bold'}}>{t('portfolioDetail.radarBenchmarkTip')}</span>
        </p>
      )}
      
      
    </div>
  );
}